'use client';

import {useState} from 'react';
import {ChevronDown, ChevronUp, Lock} from 'lucide-react';
import {track} from 'iq-tracking';
import type {InterviewGuideData} from 'iq-interview_guides/types';
import {OutcomeBadge} from 'iq-interview_experiences/components/outcome_badge';
import {ExperienceBody} from 'iq-interview_experiences/components/experience_body';
import UnlockInterviewGuide from 'iq-upselling/components/unlock_interview_guide';
import {SubmitExperienceModal} from 'iq-interview_experiences/components/submit_experience_modal';
import {DotSeparator} from 'common/components/dot_separator';
import {formatDateToQuarter} from 'common/utils';
import {InterviewExperience} from 'iq-interview_experiences/types';

interface InterviewExperienceCardProps {
  interviewExperience: InterviewExperience;
  companyName: InterviewGuideData['company_name'];
  companySlug: InterviewGuideData['company_slug'];
  companyId: InterviewGuideData['company_id'];
  positionId: InterviewGuideData['position_id'];
  positionName: InterviewGuideData['position_name'];
  positionDisplayName: InterviewExperience['position_display_name'];
  positionSlug: InterviewGuideData['position_slug'];
  visibleExperiencesCount: number;
  guideSlug: string;
}

export default function InterviewExperienceCard({
  interviewExperience,
  companyName,
  companySlug,
  visibleExperiencesCount,
  positionName,
  positionDisplayName,
  positionSlug,
  companyId,
  positionId,
  guideSlug,
}: InterviewExperienceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const isLocked = interviewExperience.body === null;

  const positionLabel =
    positionDisplayName !== null && positionDisplayName !== '' ? positionDisplayName : positionName;

  function handleClick() {
    if (isLocked) {
      void track({
        event: 'experience_lock_hit',
        action: 'open',
        component: 'barrier',
        content_type: 'experience',
        content_identifier: String(interviewExperience.id),
      });
      setUnlockOpen(true);
    } else {
      if (!isExpanded) {
        void track({
          event: 'experience_expanded',
          action: 'expand',
          component: 'card',
          content_type: 'experience',
          content_identifier: String(interviewExperience.id),
        });
      }
      setIsExpanded((prev) => !prev);
    }
  }

  return (
    <>
      <div className='bg-white border border-iq-grey-light rounded-lg overflow-hidden'>
        {/* Header row */}
        <button
          className='w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors'
          onClick={handleClick}>
          {/* Metadata */}
          <div className='text-iq-grey-dark flex-1 flex flex-col gap-0.5'>
            <div>
              <strong>{positionLabel}</strong>
              <DotSeparator className='mx-2' />
              <span>Interviewed {formatDateToQuarter(interviewExperience.interview_date)}</span>
            </div>
            {interviewExperience.preview && !isExpanded && (
              <div className=' text-iq-grey-medium'>{interviewExperience.preview}... </div>
            )}
          </div>

          {/* Outcome badge */}
          <div className='w-48 flex justify-end shrink-0'>
            {interviewExperience.outcome !== null && (
              <OutcomeBadge
                outcome={interviewExperience.outcome}
                outcome_color={interviewExperience.outcome_color}
              />
            )}
          </div>

          {/* Lock + chevron */}
          <div className='flex items-center gap-2 shrink-0 text-iq-grey-medium'>
            {isLocked && <Lock size={14} />}
            {!isLocked && (isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
          </div>
        </button>

        {/* Expanded content */}
        {interviewExperience.body && (
          <div
            className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
              isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}>
            <div className='overflow-hidden'>
              <ExperienceBody
                body={interviewExperience.body}
                prepTip={interviewExperience.prep_tip}
              />
            </div>
          </div>
        )}
      </div>

      {unlockOpen && (
        <UnlockInterviewGuide
          company={companyName}
          totalCount={visibleExperiencesCount}
          onClose={() => setUnlockOpen(false)}
          onShare={() => {
            setUnlockOpen(false);
            setShareOpen(true);
          }}
        />
      )}

      {shareOpen && (
        <SubmitExperienceModal
          guideSlug={guideSlug}
          onClose={() => setShareOpen(false)}
          defaultCompanySlug={companySlug}
          defaultPositionSlug={positionSlug}
          companyId={companyId}
          positionId={positionId}
        />
      )}
    </>
  );
}
